import { createTheme } from '@mui/material/styles';

// Custom theme based on VeraBIND style guidelines
const theme = createTheme({
  palette: {
    primary: {
      main: '#8AC7BC', // primary tint (teal)
      contrastText: '#000000', // black text on primary backgrounds
    },
    secondary: {
      main: '#97999B', // secondary accents (not text)
    },
    error: {
      main: '#FF585D', // PMS 178 C
    },
    // Dark grey token retained; placeholders use medium grey below
    greyDark: '#6E6E6E',
    text: {
      primary: '#000000', // all text black
      secondary: '#000000', // all text black
    },
  },
  typography: {
    // Base font for body copy
    fontFamily: '"ITC Avant Garde Book", Arial, sans-serif',
    // Headlines (h1-h6) use Bold, black color
    h1: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    h2: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    h3: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    h4: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    h5: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    h6: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    // Subheadings: Bold, black color
    subtitle1: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    subtitle2: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    // Body text: Book, black color
    body1: {
      fontFamily: '"ITC Avant Garde Book", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    body2: {
      fontFamily: '"ITC Avant Garde Book", Arial, sans-serif',
      textTransform: 'none',
      color: '#000000',
    },
    // Buttons: no uppercase transform, bold font
    button: {
      fontFamily: '"ITC Avant Garde Bold", Arial, sans-serif',
      textTransform: 'none',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        // Avoid teal text-only buttons on white backgrounds
        textPrimary: {
          color: '#000000',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
          },
        },
        // Primary contained buttons: teal background with black text
        containedPrimary: {
          color: '#000000',
        },
        // Outlined buttons: black text, black border, white background
        outlined: {
          color: '#000000',
          borderColor: '#000000',
          '& .MuiSvgIcon-root': { color: '#000000' },
          '&:hover': {
            borderColor: '#000000',
            backgroundColor: 'rgba(0,0,0,0.04)',
          },
        },
        outlinedPrimary: {
          color: '#000000',
          borderColor: '#000000',
        },
        outlinedSecondary: {
          color: '#000000',
          borderColor: '#000000',
        },
        outlinedError: {
          color: '#000000',
          borderColor: '#000000',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          // When label sits inside the field (acts like placeholder)
          color: '#97999B',
          '&.Mui-focused': { color: '#000000' },
          '&.MuiInputLabel-shrink': { color: '#000000' },
          '&.Mui-disabled': { color: '#97999B' },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#000000',
          '&.Mui-focused': { color: '#000000' },
          '&.Mui-error': { color: '#000000' },
          '&.Mui-disabled': { color: '#97999B' },
        },
      },
    },
    // Inputs: placeholders should be dark grey
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-webkit-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-moz-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&:-ms-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-ms-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-webkit-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-moz-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&:-ms-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-ms-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-webkit-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-moz-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&:-ms-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-ms-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-webkit-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-moz-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&:-ms-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
          '&::-ms-input-placeholder': {
            color: '#97999B',
            opacity: 1,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#000000',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          color: '#000000',
          '& .MuiTypography-root': { color: '#000000' },
        },
        primary: { color: '#000000' },
        secondary: { color: '#000000' },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: 'none',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          // Keep text white; adjust error background for better contrast
          '&.MuiBadge-colorError': {
            backgroundColor: '#dd363b',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#000000',
          textTransform: 'none',
          minHeight: 40,
          borderBottom: '2px solid #8AC7BC',
          '&.Mui-selected': {
            color: '#000000',
            backgroundColor: '#8AC7BC',
            borderBottomColor: 'transparent',
          },
        },
      },
    },
  },
});

export default theme;
